<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <PrimeDialog
    :visible="modelValue"
    @update:visible="$emit('update:modelValue', $event)"
    modal
    :header="$t('dashboardAdmin.blockTypeSelector.title')"
    :style="{ width: '95vw', maxWidth: '60rem' }"
    :dismissableMask="true"
    :closeOnEscape="true"
  >
    <div class="block-type-grid">
      <button
        v-for="blockType in blockTypes"
        :key="blockType.value"
        class="block-type-card"
        @click="selectType(blockType.value)"
        :aria-label="$t('dashboardAdmin.blockTypes.' + blockType.value)"
      >
        <div class="block-type-icon">
          <i :class="blockType.icon" aria-hidden="true"></i>
        </div>
        <div class="block-type-info">
          <span class="block-type-name">
            {{ $t("dashboardAdmin.blockTypes." + blockType.value) }}
          </span>
          <span class="block-type-description">
            {{ $t("dashboardAdmin.blockDescriptions." + blockType.value) }}
          </span>
        </div>
      </button>
    </div>
  </PrimeDialog>
</template>

<script>
import Dialog from "primevue/dialog";

const BLOCK_TYPES = [
  { value: "markdown", icon: "ri-markdown-line" },
  { value: "favorites", icon: "ri-star-line" },
  { value: "chart", icon: "ri-pie-chart-line" },
  { value: "myActivity", icon: "ri-history-line" },
  { value: "pdf", icon: "ri-file-pdf-line" },
  { value: "image", icon: "ri-image-line" },
  { value: "bookmarks", icon: "ri-bookmark-line" },
  { value: "search", icon: "ri-search-line" },
  { value: "externalApps", icon: "ri-apps-line" },
];

export default {
  name: "BlockTypeSelector",
  components: {
    PrimeDialog: Dialog,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    customBlockTypes: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "select"],
  computed: {
    blockTypes() {
      return [...BLOCK_TYPES, ...this.customBlockTypes];
    },
  },
  methods: {
    selectType(type) {
      this.$emit("select", type);
      this.$emit("update:modelValue", false);
    },
  },
};
</script>

<style scoped>
.block-type-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  gap: 0.75rem;
}

.block-type-card {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius);
  background: var(--p-content-background);
  cursor: pointer;
  text-align: left;
  transition:
    border-color 0.2s,
    box-shadow 0.2s;
  width: 100%;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
}

.block-type-card:hover {
  border-color: var(--p-primary-color);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.block-type-card:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
}

.block-type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  min-width: 3rem;
  border-radius: var(--p-border-radius);
  background: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
  font-size: 1.25rem;
}

.block-type-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.block-type-name {
  font-weight: 600;
  color: var(--p-text-color);
}

.block-type-description {
  font-size: 0.825rem;
  color: var(--p-text-muted-color);
  line-height: 1.3;
}
</style>
